/**
 * Calendar export utilities — generates iCal (.ics) format.
 *
 * Works with Google Calendar, Apple Calendar, Outlook.
 * No external library needed — iCal is plain text.
 *
 * iCal spec: RFC 5545
 */

/**
 * Format a Date to iCal UTC format: YYYYMMDDTHHMMSSZ
 * @param {Date|string} date
 * @returns {string}
 */
export const formatICalDate = (date) => {
  const d = new Date(date);
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

/**
 * Escape special iCal characters.
 * @param {string} text
 * @returns {string}
 */
export const escapeICalText = (text) =>
  String(text || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');

/**
 * Generate an iCal (.ics) file content for a booking.
 * @param {Object} params
 * @param {string} params.uid — unique event ID
 * @param {string} params.summary — event title
 * @param {string} params.description — event description
 * @param {string} params.location — event location (optional)
 * @param {Date|string} params.start — booking start
 * @param {Date|string} params.end — booking end
 * @param {string} [params.organizerEmail] — organizer email
 * @param {string} [params.organizerName] — organizer name
 * @returns {string} — iCal file content
 */
export const generateICal = ({ uid, summary, description, location, start, end, organizerEmail, organizerName }) => {
  const now = formatICalDate(new Date());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ITHouse Sharetribe//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    `DTSTART:${formatICalDate(start)}`,
    `DTEND:${formatICalDate(end)}`,
    `SUMMARY:${escapeICalText(summary)}`,
    description ? `DESCRIPTION:${escapeICalText(description)}` : null,
    location ? `LOCATION:${escapeICalText(location)}` : null,
    organizerEmail ? `ORGANIZER;CN=${escapeICalText(organizerName || organizerEmail)}:mailto:${organizerEmail}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return lines.join('\r\n');
};
